import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'nodon.csv-import.show-unused-columns';
const STORAGE_EVENT = 'nodon.csv-import.show-unused-columns:change';

const readLs = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

const writeLs = (value: boolean): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, value ? '1' : '0');
  } catch {
    // Quota / disabled storage — safe to drop the preference.
  }
};

/**
 * Persisted user preference for the CSV-import table: when `true`, columns
 * that are present in the source CSV but not consumed by the import
 * pipeline (no canonical Nodon field mapping) are shown alongside the used
 * ones. Default `false` to keep the table focused on the data Nodon
 * actually consumes.
 *
 * State syncs across hook instances in the same tab via a custom event and
 * across tabs via the native `storage` event.
 */
export const useShowUnusedColumns = (): {
  showUnusedColumns: boolean;
  setShowUnusedColumns: (value: boolean) => void;
} => {
  const [value, setValue] = useState<boolean>(readLs);

  useEffect(() => {
    const sync = () => setValue(readLs());
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) sync();
    };
    window.addEventListener(STORAGE_EVENT, sync);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(STORAGE_EVENT, sync);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const setShowUnusedColumns = useCallback((next: boolean) => {
    writeLs(next);
    setValue(next);
    window.dispatchEvent(new Event(STORAGE_EVENT));
  }, []);

  return { showUnusedColumns: value, setShowUnusedColumns };
};
